import {
  AnalyticsTrackingEvent,
  ButtonName,
  BuyBoxDialog,
  FourPointStarIcon,
  getPurchaseRewardAmountForProduct,
  isProductEligibleForCheckout,
  isProductOutOfStock,
  isShopifyProduct,
  ProductRewardSearchResult,
  useBagWords,
} from '@brandclub/common-ui';
import { styled, useTheme } from '@mui/material';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { useQuery } from '@tanstack/react-query';
import { forwardRef } from 'react';

import { useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import { appThemeQueryOptions } from '@/api/cache/appThemeQueryOptions';
import { selectCartProducts, useAllCarts, useCartUpdates } from '@/api/cache/cartQueryOptions';
import { ShopAppButton } from '@/components/button/ShopAppButton';
import PurchaseRewardDialog from '@/components/dialog/PurchaseRewardDialog';
import { useTrackActions } from '../../hooks/useTracking';
import { formatMoney } from '../../utils/string';
import BagDrawer from '../BagButton/BagDrawer';
import { getProductsToListForBuyBox } from './inpageAddToBagHelpers';

/** The default label when the theme sets none. `${price}` is filled in later. */
const defaultAddToBagText = (bag: string) => `Add to ${bag}  •  \${price}`;

const AddToBagButtonContainer = styled('div', {
  name: 'AddToBagButtonContainer',
  slot: 'Root',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '16px',
  // Match [0, xs)
  [theme.breakpoints.down('xs')]: {
    width: theme.componentCSSOverrides?.[ButtonName.AddToBagButton]?.xs?.width || 'fit-content',
  },
  // Match [xs, ∞)
  [theme.breakpoints.up('xs')]: {
    width: theme.componentCSSOverrides?.[ButtonName.AddToBagButton]?.xs?.width || 'fit-content',
  },
  // Match [md, ∞)
  [theme.breakpoints.up('md')]: {
    width: theme.componentCSSOverrides?.[ButtonName.AddToBagButton]?.md?.width || 'fit-content',
  },
}));

export const AddToBagButton = forwardRef<
  HTMLDivElement,
  {
    mobile?: boolean;
    currentBrandclubProduct?: ProductRewardSearchResult;
    /** The original product (before priority selection) — used to build the full BuyBox list */
    originalProduct?: ProductRewardSearchResult;
    onAddToCart: () => void;
    onViewRetailer: () => void;
    isEligibleForCheckout: boolean;
    allowNonEligibleClick?: boolean;
    disabled?: boolean;
    buyButtonOnly?: boolean;
    hideRewards?: boolean;
    nonEligibleButtonText?: string;
  }
>(
  (
    {
      currentBrandclubProduct,
      originalProduct,
      onAddToCart,
      onViewRetailer,
      allowNonEligibleClick,
      disabled,
      isEligibleForCheckout: isEligibleForCheckoutProp,
      buyButtonOnly,
      hideRewards,
      nonEligibleButtonText,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [trackCheckoutBarAction] = useTrackActions();
    const { data: domainConfig } = useDomainConfig();
    const { mutate: updateCart } = useCartUpdates();

    // Get Split treatment directly in this component to avoid timing issues
    const { treatments } = useSplitTreatments({
      names: ['brandclub_checkout_disabled_domain'],
      attributes: {
        domain: window.location.hostname,
        bcSplit: (window as any).bcSplit,
      },
    });
    const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

    // Apply the domain disable check here to ensure we always use the latest value
    const isEligibleForCheckout = isEligibleForCheckoutProp && !disableCheckoutByDomain;

    const showBuyBoxOnNonEligibleClick = !!theme.settings?.featureFlags?.showBuyBoxOnNonEligibleClick;

    const { data: cartProducts } = useAllCarts({
      select: selectCartProducts,
      // only read from cache to avoid resetting updated local carts
      enabled: false, // Prevents fetching
      staleTime: Infinity, // Prevents background refetch
    });
    const { data: styleData } = useQuery(appThemeQueryOptions());
    void styleData;
    const { bag } = useBagWords();

    if (!currentBrandclubProduct) {
      return null;
    }

    const addToBagText = theme.content?.addToBagButton?.signedOutDesktop?.text || defaultAddToBagText(bag);

    const isShopifyCheckout = isShopifyProduct(currentBrandclubProduct?.retailerId);
    const isButtonDisabled = disabled || (isShopifyCheckout && !isEligibleForCheckout && !allowNonEligibleClick);

    // "Not eligible for checkout" and "out of stock" are different facts, and
    // the label used to conflate them: any Shopify product that failed
    // `isProductEligibleForCheckout` was labelled "Sold out". Eligibility also
    // fails when the APP simply has no checkout capability — which is every
    // self-serve storefront, since those shoppers check out through the store's
    // own cart. So Brandclub's button sat next to the store's working "Add to
    // cart" saying "Sold out" about a product with stock on hand, on every PDP.
    // Only claim sold out when the product actually is.
    const isOutOfStock = isProductOutOfStock(currentBrandclubProduct, domainConfig?.checkoutAllowedOfferType);

    const priceAmount = formatMoney(currentBrandclubProduct?.retailPrice || 0);

    const displayAddToBagText = isEligibleForCheckout
      ? addToBagText
          .replace('${price}', (currentBrandclubProduct?.retailerDisplayName || '') + ' ' + priceAmount)
          .replace('${price_without_retailer}', priceAmount)
          .replace(/\s+/g, ' ')
          .trim()
      : nonEligibleButtonText
        ? nonEligibleButtonText
        : isShopifyCheckout && isOutOfStock
          ? 'Sold out'
          : theme.content?.plpAddToBagButton?.signedOutDesktop?.nonEligibleText ||
            theme.content?.plpAddToBagButton?.signedOutDesktop?.text ||
            theme.content?.addToBagButton?.signedOutDesktop?.nonEligibleText ||
            'View';

    const displayAddToBagContent = (() => {
      const priceIndex = displayAddToBagText.indexOf(priceAmount);
      if (priceIndex > -1) {
        const before = displayAddToBagText.slice(0, priceIndex);
        const after = displayAddToBagText.slice(priceIndex + priceAmount.length);
        return (
          <>
            {before}
            <span className="bc_price">&nbsp;{priceAmount}</span>
            {after}
          </>
        );
      }
      return displayAddToBagText;
    })();

    const cartProduct = currentBrandclubProduct?.stacklineSku
      ? cartProducts?.[currentBrandclubProduct?.stacklineSku]
      : currentBrandclubProduct;

    const metadata = cartProduct ?? currentBrandclubProduct;

    const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);

    const addToBagStyleOverride = theme.componentCSSOverrides?.[ButtonName.AddToBagButton];

    const height = {
      xs: addToBagStyleOverride?.xs?.height,
      md: addToBagStyleOverride?.md?.height,
    };

    const earnRewardButtonStyleOverride = addToBagStyleOverride;
    delete earnRewardButtonStyleOverride?.md?.color;
    delete earnRewardButtonStyleOverride?.xs?.color;

    // Handler for BuyBox product clicks
    const handleBuyBoxProductClick = (product: ProductRewardSearchResult) => {
      const isProductEligible = isProductEligibleForCheckout(product, domainConfig?.checkoutAllowedOfferType);
      if (isProductEligible && !disableCheckoutByDomain) {
        updateCart({
          action: 'add',
          items: [{ ...product, quantity: 1 }],
          retailerId: product.retailerId,
        });
        trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          quantity: 1,
          source: 'brandclub_dtc_checkout_bar',
        });
        BagDrawer?.show();
      } else {
        trackCheckoutBarAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          source: 'brandclub_dtc_checkout_bar',
        });
        window.open(product.retailerProductPageUrl, '_blank');
      }
    };

    // When showBuyBoxOnNonEligibleClick is on, open BuyBox instead of navigating to retailer
    const handleNonEligibleClick = () => {
      if (showBuyBoxOnNonEligibleClick && currentBrandclubProduct) {
        const sourceProduct = originalProduct || currentBrandclubProduct;
        const productsToList = getProductsToListForBuyBox(sourceProduct);
        BuyBoxDialog.show({
          products: productsToList,
          checkoutAllowedOfferType: domainConfig?.checkoutAllowedOfferType,
          disabledCheckout: disableCheckoutByDomain,
          onClick: handleBuyBoxProductClick,
          onProductPageClick: (product) => {
            trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
              stacklineSku: product.stacklineSku,
              retailerId: product.retailerId,
              retailerSku: product.retailerSku,
              retailPrice: product.retailPrice,
              organicType: 'openRetailerProductPage',
              source: 'brandclub_dtc_buybox_non_eligible',
            });
            window.open(product.retailerProductPageUrl, '_blank');
          },
        });
      } else {
        onViewRetailer();
      }
    };

    return (
      <AddToBagButtonContainer ref={ref}>
        <ShopAppButton
          height={height}
          disableMaxWidth
          disabled={isButtonDisabled}
          sx={{
            width: '100%',
            ...addToBagStyleOverride,
            // need to use && here because ShopAppButton uses `increaseSpecificity` to avoid having styles overwritten by DTC sites
            '&&': {
              fontWeight: theme.typography.lgButton.fontWeight,
              [theme.breakpoints.down('xs')]: {
                fontWeight: theme.typography.button.fontWeight,
              },
            },
          }}
          className="bc-add-to-bag-button "
          variant="solid"
          onClick={() => {
            if (isEligibleForCheckout) {
              onAddToCart();
            } else {
              handleNonEligibleClick();
            }
          }}
          type="button"
        >
          {displayAddToBagContent}
        </ShopAppButton>

        {!!rewardAmount && !buyButtonOnly && !hideRewards && (
          <ShopAppButton
            disableMaxWidth
            height={height}
            sx={{
              width: '100%',
              ...earnRewardButtonStyleOverride,

              fontWeight: theme.typography.lgButton.fontWeight,
              [theme.breakpoints.down('xs')]: {
                fontWeight: theme.typography.button.fontWeight,
              },
            }}
            className="bc-earn-reward-button"
            variant="outline"
            onClick={() => {
              PurchaseRewardDialog.show(rewardAmount);
              trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_VIEW_PURCHASE_BENEFITS, {
                stacklineSku: currentBrandclubProduct?.stacklineSku,
                retailerId: currentBrandclubProduct?.retailerId,
              });
            }}
            type="button"
          >
            <FourPointStarIcon style={{ width: 16, height: 16, marginRight: 8 }} />
            Earn {formatMoney(rewardAmount)} in cash rewards
          </ShopAppButton>
        )}
      </AddToBagButtonContainer>
    );
  },
);
